import { useState } from 'react'

import { FxAlert, FxButton, FxIcon, FxPanel } from '@/components/fx'
import type { DocumentCounts, Job } from '@/types/domain'

import { CockpitDialog } from './CockpitDialog'
import { documentCountSubject, runProgress } from './documentsModel'
import { documentActionError } from './useDocuments'

/**
 * "Analyse läuft": the strip between the status panels and the table while a
 * job of this template is pending or processing.
 *
 * The bar is one figure the worker reports (`progress`, a percentage) spread
 * over the documents the jobs carry, so everything derived from it is
 * labelled "etwa". The counted numbers — in Verarbeitung, ohne Treffer —
 * come from the status counts and are exact.
 */
export function ProcessingStrip({
  activeJobs,
  counts,
  canWrite,
  onCancel,
  cancelling,
  cancelError,
}: {
  activeJobs: Job[]
  counts?: DocumentCounts
  canWrite: boolean
  onCancel: (jobIds: string[]) => void
  cancelling: boolean
  cancelError: unknown
}) {
  const [confirming, setConfirming] = useState(false)
  const progress = runProgress(activeJobs, counts)
  if (!progress) return null

  const donePercent = progress.percent
  const runningPercent = progress.total > 0 ? (progress.processing / progress.total) * 100 : 0

  return (
    <FxPanel className="flex flex-col gap-3 p-5 sm:px-6">
      <div className="flex flex-wrap items-center gap-3">
        <FxIcon name="autorenew" size={22} className="text-warning-text" />
        <b className="text-body-strong">Analyse läuft</b>
        {progress.startedAt && (
          <span className="text-small text-text-muted">gestartet {progress.startedAt} Uhr</span>
        )}
        <span className="flex-1" />
        {canWrite && (
          <FxButton variant="outlined" onClick={() => setConfirming(true)} disabled={cancelling}>
            {cancelling ? 'Wird abgebrochen …' : 'Abbrechen'}
          </FxButton>
        )}
      </div>

      {/* The bar is decoration for the sentence under it: the same figures are
          written out, so nothing depends on reading a colour. */}
      <div className="flex h-1.5 overflow-hidden rounded-full bg-fill" aria-hidden="true">
        <span className="bg-success" style={{ width: `${donePercent}%` }} />
        <span className="bg-warning" style={{ width: `${runningPercent}%` }} />
      </div>
      <p className="m-0 text-small">
        etwa {progress.done} von {progress.total} fertig · {progress.processing} in Verarbeitung ·{' '}
        {progress.queued} wartend
      </p>

      {cancelError != null && <FxAlert tone="danger">{documentActionError(cancelError)}</FxAlert>}

      <CockpitDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Analyse abbrechen?"
        description={
          <>
            Der Auftrag über {documentCountSubject(progress.total)} wird gestoppt. Dokumente, die
            gerade gelesen werden, laufen noch zu Ende; alle übrigen gehen zurück auf „noch nicht
            verarbeitet“. Fertige Ergebnisse bleiben erhalten.
          </>
        }
        footer={
          <>
            <FxButton variant="outlined" onClick={() => setConfirming(false)}>
              Weiterlaufen lassen
            </FxButton>
            <FxButton
              variant="danger"
              disabled={cancelling}
              onClick={() => {
                onCancel(activeJobs.map((job) => job.id))
                setConfirming(false)
              }}
            >
              Analyse abbrechen
            </FxButton>
          </>
        }
      />
    </FxPanel>
  )
}
